import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ProjectDetailDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { EmptyState, Panel } from "@/design/primitives";
import { relativeTime } from "@/lib/format";

const SEV_COLOR: Record<string, string> = {
  P0: "var(--critical)",
  P1: "var(--critical)",
  P2: "var(--warning)",
  P3: "var(--text-secondary)",
};

/** INCIDENTS TAB (spec §62): lifecycle přehled, aktivní první. */
export function IncidentsTab({ project }: { project: ProjectDetailDTO }) {
  const query = useQuery({
    queryKey: ["section", project.id, "incidents"],
    queryFn: () => api.projectIncidents(project.id),
  });
  const list = query.data?.incidents ?? [];
  const active = list.filter((i) => i.status !== "RESOLVED");

  return (
    <div className="mt-4 max-w-[900px]">
      <div className="mb-3 flex items-center justify-between">
        <span className="af-label-dim">
          {cs.incidents.title} // {cs.incidents.active(active.length)}
        </span>
      </div>
      {list.length === 0 ? (
        <Panel className="p-8">
          <EmptyState title={cs.incidents.empty} />
        </Panel>
      ) : (
        <div className="flex flex-col gap-2.5">
          {list.map((incident) => {
            const open = incident.status !== "RESOLVED";
            const sev = SEV_COLOR[incident.severity];
            return (
              <Link
                key={incident.id}
                to="/incidents/$id"
                params={{ id: incident.id }}
                className="block px-4 py-3"
                style={{ borderRadius: "var(--radius-box)",
                  border: `1px solid ${open ? "color-mix(in srgb, var(--critical) 35%, transparent)" : "var(--border-subtle)"}`,
                  background: open ? "color-mix(in srgb, var(--critical) 3%, transparent)" : "transparent",
                }}
                data-testid="incident-row"
              >
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
                  <span className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-0.5">
                    <span className={open ? "af-animate-pulse text-[11px] font-bold tracking-[0.1em]" : "text-[11px] font-bold tracking-[0.1em]"} style={{ color: sev }}>
                      {incident.label}
                    </span>
                    <span className="min-w-0 break-words text-[11px]" style={{ color: "var(--text-primary)" }}>
                      {incident.title}
                    </span>
                  </span>
                  <span className="flex-none text-[11px] tracking-[0.08em]" style={{ color: open ? sev : "var(--positive)" }}>
                    {incident.status === "OPEN" ? cs.incidents.statusOpen : incident.status === "MONITORING" ? cs.incidents.statusMonitoring : cs.incidents.statusResolved}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap justify-between gap-x-3 gap-y-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                  <span>
                    {incident.source} · {relativeTime(incident.startedAt)}
                    {incident.hotfixTask ? ` · hotfix #${incident.hotfixTask.number} (EP ${incident.hotfixTask.epScore})` : ""}
                  </span>
                  <span>{incident.errorRate ?? ""}</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
